import { useCallback, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ObjectBrowser } from '@/components/ObjectBrowser';
import type { BrowserFetcherResult, BrowserMapConfig } from '@/components/ObjectBrowser';
import { InitiativeCard } from '@/components/initiatives/InitiativeCard';
import { initiativesApi } from '@/lib/api';
import type { ApiInitiative } from '@/lib/api/types';
import type { MapBrowserObject } from '@/components/ObjectMapBrowser';
import { getInitiativeHighlight, INITIATIVE_TIMING_LEGEND } from '@/components/ObjectMapBrowser';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
import { useSession } from '@/lib/auth-client';
import { DatePickerWithRange } from '@/components/custom/date-picker-with-range';
import type { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import { INITIATIVE_TAG_OPTIONS, getInitiativeUrl, RelativeDatePresents, formatEventDateRange } from '@sizzlenz/shared';
import { VipSignInBanner } from '@/components/auth/VipSignInBanner';
import { useInitiativeDetailSheet, buildInitiativeNavigationList } from '@/lib/hooks/useInitiativeDetailSheet';
import { WHATSON_URL_SYNC_FILTERS, getWhatsOnFilterConfigs, buildWhatsOnInitialFilters, syncWhatsOnFilters, buildWhatsOnApiQuery } from '@/lib/whatson-filters';
import { OpportunitiesQuickFilters } from '@/components/initiatives/OpportunitiesQuickFilters';
function initiativeToMapObject(initiative: ApiInitiative): MapBrowserObject {
  const addressData = (initiative.data as Record<string, any>)?.address_1;
  const lat = addressData?.geometry?.location?.lat;
  const lng = addressData?.geometry?.location?.lng;
  const tagLabels = (initiative.tags ?? []).map(tagKey => INITIATIVE_TAG_OPTIONS.find(t => t.id === tagKey)?.label).filter(Boolean) as string[];
  return {
    id: initiative.id,
    objectType: 'initiative',
    lat: lat ?? undefined,
    lng: lng ?? undefined,
    title: initiative.name,
    image: (initiative as any).image ?? undefined,
    href: getInitiativeUrl(initiative.type, initiative.id),
    tags: tagLabels,
    data: initiative,
    highlight: getInitiativeHighlight(initiative.startDate, initiative.endDate),
    dateRange: formatEventDateRange(initiative.startDate, initiative.endDate) || undefined
  };
}
export interface OpportunitiesBrowserProps {
  /** Page title shown in the header */
  title: string;
  /** Subtitle shown below the title */
  subtitle?: ReactNode;
  /** Meta title for the page head */
  metaTitle?: string;
  /** Meta description for the page head */
  metaDescription?: string;
  /**
   * Hard-coded filters that are always applied to API queries regardless of user selection.
   * These filter keys will be hidden from the UI filter panel.
   * e.g. { categories: ['consumer'] } will always filter by consumer category.
   */
  hardCodedFilters?: Record<string, string[]>;
  /** Whether to show the sign-in banner for unauthenticated users. Defaults to true. */
  showSignInBanner?: boolean;
  /** Redirect path after sign-in. Defaults to current page. */
  signInRedirectTo?: string;
  /** Whether to show the date range filter. Defaults to true. */
  showDateFilter?: boolean;
  /** Number of items per page. Defaults to 40. */
  limit?: number;
  /** Map configuration overrides. Merged with the component's defaults (defaultShowMap: true, showMapOnEmpty: true, legend, onShowDetail). */
  mapConfig?: Partial<BrowserMapConfig>;
  /** Whether to show the owner on each InitiativeCard. Defaults to false. */
  showOwner?: boolean;
  /** Show the initiative status badge (e.g. "Gauging Interest") */
  showStatus?: boolean;
  /** Control when to display the interest count badge */
  showInterestCount?: 'never' | 'always' | 'non-zero';
}
export function OpportunitiesBrowser({
  title,
  subtitle,
  metaTitle,
  metaDescription,
  hardCodedFilters = {},
  showSignInBanner = true,
  signInRedirectTo,
  showInterestCount,
  showStatus,
  showDateFilter = true,
  limit = 40,
  mapConfig: mapConfigOverrides,
  showOwner = false
}: OpportunitiesBrowserProps) {
  const queryClient = useQueryClient();
  const {
    data: userSession
  } = useSession();
  const isAdmin = !!userSession?.user?.isAdmin;
  const {
    showInitiativeDetail
  } = useInitiativeDetailSheet();
  const handleShowDetail = useCallback((obj: MapBrowserObject, allObjects?: MapBrowserObject[]) => {
    if (obj.objectType === 'initiative') {
      const initiativeObjects = (allObjects || []).filter(o => o.objectType === 'initiative');
      const navigationList = initiativeObjects.length > 1 ? buildInitiativeNavigationList(initiativeObjects.map(o_0 => o_0.data as ApiInitiative)) : undefined;
      showInitiativeDetail(obj.data as ApiInitiative, {
        navigationList
      });
    }
  }, [showInitiativeDetail]);
  const {
    values,
    handlers,
    isReady
  } = useUrlSync({
    filters: WHATSON_URL_SYNC_FILTERS
  });
  const dateRange: DateRange | undefined = values.dateFrom || values.dateTo ? {
    from: values.dateFrom ? new Date(values.dateFrom) : undefined,
    to: values.dateTo ? new Date(values.dateTo) : undefined
  } : undefined;
  const handleDateRangeChange = useCallback((newDateRange: DateRange | undefined) => {
    handlers.dateFrom(newDateRange?.from?.toISOString() || '');
    handlers.dateTo(newDateRange?.to?.toISOString() || '');
  }, [handlers.dateFrom, handlers.dateTo]);

  // Build filter configs, removing any filters that are hard-coded
  const filterConfigs = useMemo(() => {
    const allConfigs = getWhatsOnFilterConfigs(isAdmin);
    const hardCodedKeys = Object.keys(hardCodedFilters);
    if (hardCodedKeys.length === 0) return allConfigs;
    return allConfigs.filter(config => !hardCodedKeys.includes(config.key));
  }, [isAdmin, hardCodedFilters]);
  const initialFilters = useMemo(() => buildWhatsOnInitialFilters(values, isAdmin), [values.categories.join(','), values.type.join(','), values.activities.join(','), values.occasions.join(','), values.status.join(','), isAdmin]);
  const fetcher = useCallback(async (params: {
    search?: string;
    filters: Record<string, string[]>;
    cursor?: string;
    limit: number;
  }): Promise<BrowserFetcherResult<ApiInitiative>> => {
    // Merge hard-coded filters into the user-selected filters
    const mergedFilters = {
      ...params.filters
    };
    for (const [key, value] of Object.entries(hardCodedFilters)) {
      mergedFilters[key] = value;
    }
    const query = buildWhatsOnApiQuery({
      ...params,
      filters: mergedFilters,
      dateFrom: values.dateFrom,
      dateTo: values.dateTo,
      tags: values.tags.length > 0 ? values.tags : undefined
    });
    const response = await queryClient.fetchQuery({
      queryKey: ['initiatives', query],
      queryFn: () => initiativesApi.getInitiatives(query),
      staleTime: 1000 * 10
    });
    return {
      objects: response.initiatives,
      hasNextPage: !!response.pagination?.nextCursor,
      nextCursor: response.pagination?.nextCursor ?? undefined
    };
  }, [values.dateFrom, values.dateTo, values.tags, queryClient, hardCodedFilters]);
  const handleSearchChange = useCallback((search: string) => handlers.search(search), [handlers.search]);
  const handleFiltersChange = useCallback((filters: Record<string, string[]>) => syncWhatsOnFilters(filters, handlers, isAdmin), [handlers.categories, handlers.type, handlers.activities, handlers.occasions, handlers.status, isAdmin]);
  const whenDatePresents = useMemo(() => {
    return RelativeDatePresents.filter(p => !p.id.startsWith('last') && p.id !== 'yesterday');
  }, []);
  const dateChip = useMemo(() => {
    if (!dateRange?.from && !dateRange?.to) return null;
    const fromStr = dateRange?.from ? format(dateRange.from, 'MMM d, yyyy') : '';
    const toStr = dateRange?.to ? format(dateRange.to, 'MMM d, yyyy') : '';
    const label = fromStr && toStr ? `${fromStr} – ${toStr}` : fromStr || toStr;
    return <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
        {label}
        <button onClick={() => handleDateRangeChange(undefined)} className="hover:text-primary-900 cursor-pointer">
          <X className="w-3 h-3" />
        </button>
      </span>;
  }, [dateRange, handleDateRangeChange]);
  if (!isReady) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            {subtitle && <div className="text-sm text-gray-500 mt-1">{subtitle}</div>}
          </div>
        </div>
        {showSignInBanner ? <VipSignInBanner redirectTo={signInRedirectTo || '/opportunities'} /> : null}
        <OpportunitiesQuickFilters className="mb-3" value={initialFilters} onChange={handleFiltersChange} />
        <ObjectBrowser<ApiInitiative> fetcher={fetcher} fetchKey={`${values.dateFrom}|${values.dateTo}|${values.tags.join(',')}`} filterSlot={showDateFilter ? <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">When</label>
                <div className="flex flex-wrap items-center gap-2">
                  <DatePickerWithRange presets={whenDatePresents} date={dateRange} setDate={handleDateRangeChange} clearable={true} />
                  {dateRange && <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 cursor-pointer" onClick={() => handleDateRangeChange(undefined)} title="Clear date range">
                      <X className="h-4 w-4" />
                    </Button>}
                </div>
              </div> : undefined} renderCard={initiative => <InitiativeCard showInterestCount={showInterestCount} showStatus={showStatus} initiative={initiative} variant="compact" showOwner={showOwner} />} getKey={initiative_0 => initiative_0.id} filters={filterConfigs} searchEnabled searchPlaceholder="Search..." limit={limit} emptyState={{
        title: 'No matches',
        description: 'Try adjusting your search, filters, or date range'
      }} initialSearch={values.search} initialFilters={initialFilters} onSearchChange={handleSearchChange} onFiltersChange={handleFiltersChange} filterChipSlot={dateChip} toMapObject={initiativeToMapObject} mapConfig={{
        defaultShowMap: true,
        showMapOnEmpty: true,
        onShowDetail: handleShowDetail,
        legend: INITIATIVE_TIMING_LEGEND,
        ...mapConfigOverrides
      }} />
      </div>
    </div>;
}